import app from 'flarum/forum/app';
import { override, extend } from 'flarum/common/extend';
import NewsPage from './NewsPage';
import vouchaSaved from './Saved';
import DiscussionControls from 'flarum/forum/utils/DiscussionControls';
import Button from 'flarum/common/components/Button';
import DiscussionListState from 'flarum/forum/states/DiscussionListState';

/**
 * "For you" (members) and "Popular" (everyone) at the top of the sort menu, and used as the
 * default when no sort is chosen. Searches keep their relevance order.
 */
app.initializers.add('voucha-feed', () => {
  // AI disclosure: posts by the news accounts (TLDRNews) say so in the byline, at the top of the post
  extend('flarum/forum/components/CommentPost', 'headerItems', function (items) {
    const user = this.attrs.post && this.attrs.post.user();
    if (!user || !user.attribute('vouchaAiAuthor')) return;
    items.add('vouchaAi', m("span", {
      className: "VouchaAiLabel",
      title: "Written with AI from the news reports linked in the post"
    }, m("i", {
      className: "fas fa-robot",
      "aria-hidden": "true"
    }), " AI-generated"), -10);
  });
  override(DiscussionListState.prototype, 'sortMap', function (original) {
    const map = original();
    if (this.params.q) return map;
    const first = {};
    if (app.session.user) first.foryou = {
      sort: '-vouchaForYou',
      label: app.translator.trans('voucha-feed.forum.sort.foryou')
    };
    first.popular = {
      sort: '-vouchaPopular',
      label: app.translator.trans('voucha-feed.forum.sort.popular')
    };
    return {
      ...first,
      ...map
    };
  });

  // With no sort chosen, Flarum would ask the server for "latest" while the menu shows the first
  // entry; default to the first entry so the two agree.
  override(DiscussionListState.prototype, 'getSort', function (original) {
    const chosen = original();
    if (chosen || this.params.q) return chosen;
    return app.session.user ? 'foryou' : 'popular';
  });
});

/**
 * Hide sort options chosen in Admin > Voucha Feed ("Hidden sort options", e.g. "az, za").
 * Registered with a low priority so it runs after every extension has added its sorts
 * (Trending and Upvotes come from gamification). A hidden sort still works from an old link.
 */
app.initializers.add('voucha-feed-hide-sorts', () => {
  override(DiscussionListState.prototype, 'sortMap', function (original) {
    const map = original();
    const hidden = String(app.forum.attribute('vouchaFeedHiddenSorts') || '').split(',').map(k => k.trim().toLowerCase().replace(/\s+/g, '')).filter(Boolean);
    if (!hidden.length) return map;
    // Plain words as well as internal names: "trending" and "upvotes" are what the menu shows;
    // FoF Gamification names them "hot"/"hotness" and "votes" internally
    const ALIASES = {
      trending: ['hot', 'hotness', 'trending'],
      upvotes: ['votes', 'vote', 'upvotes', 'mostvotes'],
      hot: ['hot', 'hotness'],
      votes: ['votes', 'vote']
    };
    const names = new Set();
    hidden.forEach(h => (ALIASES[h] || [h]).forEach(n => names.add(n)));
    const keep = this.params.sort; // the sort in use stays, so old links keep working
    const out = {};
    Object.keys(map).forEach(key => {
      const k = key.toLowerCase().replace(/[^a-z]/g, '');
      if (!names.has(k) || key === keep) out[key] = map[key];
    });
    return out;
  });
}, -100);

/**
 * Bot news apart from the home feed. On the main feed only (no tag, no search), ask the server for
 * filter[home]: news accounts' posts show only once people are discussing them. Tag pages,
 * Following, search and My Community are unchanged. News on the home feed gets a "News" label;
 * all news lives on /news.
 */
app.initializers.add('voucha-feed-saved', () => vouchaSaved());
app.initializers.add('voucha-feed-news', () => {
  app.routes['voucha-feed.news'] = {
    path: '/news',
    component: NewsPage
  };

  // The home feed is the forum's root address (with or without a sort), with no tag and no search
  const isHomeFeed = state => {
    const path = window.location.pathname.replace(/\/+$/, '');
    const base = (app.forum.attribute('basePath') || '').replace(/\/+$/, '');
    return path === base && !state.params.tags && !state.params.q;
  };
  extend(DiscussionListState.prototype, 'requestParams', function (params) {
    if (isHomeFeed(this)) {
      params.filter = params.filter || {};
      params.filter.home = 1;
    }
  });

  // The server sends the first page of discussions with the page itself, built the plain way:
  // newest first and without the home feed's news filter. Flarum shows it as-is, so the first view
  // was "latest, TLDR news included" until the list was next loaded. Set it aside whenever the
  // browser's own request would differ (the home feed, or no sort chosen, where the browser uses
  // For you / Popular) so the first page is the right one.
  override(DiscussionListState.prototype, 'loadPage', function (original, page) {
    if (page === void 0) {
      page = 1;
    }
    if (page === 1 && !this.params.q && (isHomeFeed(this) || !this.params.sort)) {
      app.preloadedApiDocument();
    }
    return original(page);
  });
});

/** "Make lead story" / "Remove as lead story" in a news discussion's menu (admins and moderators) */
app.initializers.add('voucha-feed-news-lead', () => {
  extend(DiscussionControls, 'moderationControls', function (items, discussion) {
    if (!discussion.attribute || !discussion.attribute('vouchaNews') || !app.forum.attribute('canVouchaNewsLead')) return;
    const isLead = String(app.forum.attribute('vouchaNewsLead') || '') === String(discussion.id());
    const set = id => app.request({
      method: 'POST',
      url: app.forum.attribute('apiUrl') + '/voucha-feed/news-lead',
      body: {
        discussionId: id
      }
    }).then(() => {
      app.forum.pushAttributes({
        vouchaNewsLead: id ? String(id) : '',
        vouchaNewsLeadAt: new Date().toISOString()
      });
      app.alerts.show({
        type: 'success'
      }, id ? 'This story is now the lead on the News page.' : 'This story is no longer the lead.');
    });
    items.add('vouchaNewsLead', m(Button, {
      icon: "fas fa-star",
      onclick: () => set(isLead ? null : discussion.id())
    }, isLead ? 'Remove as lead story' : 'Make lead story'));
  });
});